import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Shuffle as ShuffleIcon,
  RestartAlt as ClearIcon,
  SportsEsportsOutlined as MineIcon,
  LockOutlined as LockIcon,
  CheckCircleOutline as CheckIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";
import { CommonButton, CommonChip } from "../../../../Components/ui";

const MinePicker = ({
  capacity = 20,
  alreadySelected = [],
  selectednum = [],
  onSelectNumber,
  onQuickPick,
  onClear,
}) => {
  const numCapacity = Math.max(1, Number(capacity) || 20);
  const soldSet = new Set(alreadySelected.map((n) => Number(n)));
  const remainingCount = Math.max(0, numCapacity - soldSet.size);
  const selectedNumber = selectednum.length > 0 ? selectednum[0] : null;

  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.lg,
        border: `1px solid ${colors.slate[200]}`,
        boxShadow: "0 4px 15px rgba(0,0,0,0.03)",
        bgcolor: "#ffffff",
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
        {/* Header Section */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: colors.slate[900],
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <MineIcon sx={{ color: colors.purple[600] }} />
              Choose Your Ticket
            </Typography>
            <Typography variant="body2" sx={{ color: colors.slate[500], mt: 0.25 }}>
              Select <strong>1 unsold ticket</strong> from the minefield grid below.
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={1}>
            <CommonButton
              variant="purpleAction"
              size="small"
              startIcon={<ShuffleIcon fontSize="small" />}
              onClick={onQuickPick}
              disabled={remainingCount === 0}
              label="Random Available"
            />
            <CommonButton
              variant="clearAction"
              size="small"
              startIcon={<ClearIcon fontSize="small" />}
              onClick={onClear}
              disabled={selectednum.length === 0}
              label="Clear"
            />
          </Stack>
        </Stack>

        {/* Status Bar & Legend */}
        <Box
          sx={{
            bgcolor: colors.slate[50],
            border: `1px solid ${colors.slate[200]}`,
            borderRadius: borderRadiusPx.md,
            p: 1.75,
            mb: 3,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: colors.slate[700] }}>
                Ticket Availability:
              </Typography>
              <CommonChip
                label={`${remainingCount} / ${numCapacity} Available`}
                variant={remainingCount > 0 ? "active" : "ended"}
                size="small"
              />
            </Stack>

            {/* Legend */}
            <Stack direction="row" spacing={2} sx={{ fontSize: "0.75rem" }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: borderRadiusPx.circle,
                    bgcolor: "#ffffff",
                    border: `1.5px solid ${colors.purple[400]}`,
                  }}
                />
                <Typography variant="caption" sx={{ color: colors.slate[600] }}>
                  Available
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: borderRadiusPx.circle,
                    bgcolor: colors.purple[600],
                  }}
                />
                <Typography variant="caption" sx={{ color: colors.purple[700], fontWeight: 600 }}>
                  Selected
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: borderRadiusPx.circle,
                    bgcolor: colors.slate[200],
                    border: `1px solid ${colors.slate[300]}`,
                  }}
                />
                <Typography variant="caption" sx={{ color: colors.slate[400] }}>
                  Sold
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Box>

        {/* Selected Ticket Callout */}
        <Box sx={{ mb: 2.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.slate[700] }}>
              Your Selected Ticket:
            </Typography>
            {selectedNumber ? (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CheckIcon sx={{ fontSize: 16, color: colors.emerald[600] }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: colors.emerald[600] }}>
                  Ticket #{selectedNumber} Ready
                </Typography>
              </Stack>
            ) : (
              <Typography variant="caption" sx={{ color: colors.slate[400], fontStyle: "italic" }}>
                Click an unsold number below
              </Typography>
            )}
          </Stack>
        </Box>

        <Divider sx={{ mb: 2.5, borderColor: colors.slate[100] }} />

        {/* Grid of Mine Numbers */}
        <div className="buylottery-numbers-grid">
          {Array.from({ length: numCapacity }, (_, i) => {
            const num = i + 1;
            const isSold = soldSet.has(num);
            const isSelected = selectedNumber === num;

            return (
              <Tooltip
                key={num}
                title={
                  isSold
                    ? `Ticket #${num} is already sold`
                    : isSelected
                    ? `Ticket #${num} selected (click to remove)`
                    : `Click to select Ticket #${num}`
                }
              >
                <div
                  className={`mine-number-ball ${isSold ? "sold" : ""} ${
                    isSelected ? "selected" : ""
                  }`}
                  onClick={() => {
                    if (!isSold) {
                      onSelectNumber(num);
                    }
                  }}
                  role="button"
                  tabIndex={isSold ? -1 : 0}
                  onKeyDown={(e) => {
                    if (!isSold && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      onSelectNumber(num);
                    }
                  }}
                >
                  {isSold ? <LockIcon sx={{ fontSize: 14 }} /> : num}
                </div>
              </Tooltip>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default MinePicker;
